import React from 'react'
import { ICellRendererParams } from 'ag-grid-enterprise'
import { Select, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Company } from '../../../../types/company/Company'
import { alreadyMappedIdsFor, SiblingCandidates } from '../../utils/groupSuggestionsBySibling'
import { RowAction } from '../../utils/buildSiblingDecision'
import SourceRecordName from '../SourceRecordName'
import CellSignalDot from './CellSignalDot'
import WizardGridCell from './WizardGridCell'

export interface WizardSuggestionsGridContext {
  targetId: string
  candidatesBySibling: Map<string, SiblingCandidates>
  rowActions: Record<string, RowAction>
  onRowActionChange: (siblingId: string, action: RowAction) => void
}

interface CandidateCellRendererParams extends ICellRendererParams<Company, unknown, WizardSuggestionsGridContext> {
  role: 'customer' | 'supplier'
}

// Renders "already linked" and "top suggestion" as two stacked lines, since a sibling can now
// have both at once (already linked to one source record, but still have further candidates).
export const CandidateCellRenderer: React.FC<CandidateCellRendererParams> = ({ data, context, role }) => {
  const { t } = useTranslation()
  if (!data || !context) return null

  const sourceEntity = role === 'customer' ? 'Customer' : 'Supplier'
  const candidates = context.candidatesBySibling.get(data.id)
  const alreadyMappedIds = alreadyMappedIdsFor(role, candidates)
  const remaining = candidates?.[role] ?? []
  const top = remaining[0]
  const extra = remaining.length > 1 ? ` (+${remaining.length - 1})` : ''

  if (alreadyMappedIds.length === 0 && !top) {
    return (
      <WizardGridCell>
        <Typography.Text type="secondary">
          <CellSignalDot signal="none" />
          {t('groupCustomerMapping:wizardSuggestionsNoMatch', 'ei osumaa')}
        </Typography.Text>
      </WizardGridCell>
    )
  }

  return (
    <WizardGridCell>
      {alreadyMappedIds.map(sourceId => (
        <div key={sourceId}>
          <CellSignalDot signal="linked" />
          <SourceRecordName companyId={context.targetId} sourceEntity={sourceEntity} sourceId={sourceId} />
        </div>
      ))}
      {top && (
        <Typography.Text>
          <CellSignalDot signal="suggestion" />
          <SourceRecordName companyId={context.targetId} sourceEntity={sourceEntity} sourceId={top.sourceId} /> ·{' '}
          {Math.round(top.confidence * 100)} %{extra}
        </Typography.Text>
      )}
    </WizardGridCell>
  )
}

export const ActionCellRenderer: React.FC<ICellRendererParams<Company, unknown, WizardSuggestionsGridContext>> = ({
  data,
  context
}) => {
  const { t } = useTranslation()
  if (!data || !context) return null

  return (
    <Select<RowAction>
      style={{ width: '100%' }}
      value={context.rowActions[data.id] ?? 'skip'}
      onChange={value => context.onRowActionChange(data.id, value)}
      options={[
        { value: 'accept', label: t('groupCustomerMapping:wizardSuggestionsAccept', 'hyväksy') },
        { value: 'review', label: t('groupCustomerMapping:wizardSuggestionsReview', 'tarkista') },
        { value: 'skip', label: t('groupCustomerMapping:wizardSuggestionsSkip', 'ohita') }
      ]}
    />
  )
}
